import { Phone, Mail, Facebook, Instagram } from "lucide-react";
import { FB_URL, IG_URL } from "@/components/site/social";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      id="kontakt"
      data-testid="contact-section"
      className="relative bg-cm-beige overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full blur-3xl opacity-40"
        style={{ background: "radial-gradient(circle, #D9B0A8 0%, transparent 60%)" }}
      />
      <div className="relative max-w-[1280px] mx-auto px-5 sm:px-10 lg:px-16 py-16 sm:py-24 lg:py-40">
        <div className="grid lg:grid-cols-12 gap-10 sm:gap-12 lg:gap-16">
          <div className="lg:col-span-7 reveal">
            <span className="ornament" data-testid="contact-eyebrow">
              <em>Kontakt</em>
            </span>
            <h2
              data-testid="contact-headline"
              className="font-serif text-cm-ink mt-8 text-[1.85rem] sm:text-4xl lg:text-[3.2rem] leading-[1.16] sm:leading-[1.1] tracking-[-0.005em] max-w-2xl"
            >
              Jeg glæder mig til at{" "}
              <span className="marker">møde dig.</span>
            </h2>
            <p className="mt-8 text-cm-ink-soft leading-relaxed max-w-lg">
              Skriv eller ring til mig — vi finder ud af, hvad du har brug for,
              og om vi er det rette match.
            </p>
          </div>

          <div className="lg:col-span-5 reveal">
            <div className="space-y-8">
              <a
                href="tel:+4591791513"
                data-testid="contact-phone"
                className="group flex items-start gap-4 sm:gap-5 py-6 border-t border-cm-line"
              >
                <span className="mt-1 w-10 h-10 rounded-full bg-cm-cream flex items-center justify-center text-cm-rose group-hover:bg-cm-rose group-hover:text-cm-cream transition-colors duration-500 flex-shrink-0">
                  <Phone size={16} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[0.7rem] tracking-[0.3em] uppercase text-cm-ink-soft">
                    Telefon
                  </p>
                  <p className="font-serif text-cm-ink text-xl sm:text-2xl mt-2 group-hover:text-cm-rose transition-colors duration-500">
                    +45 91 79 15 13
                  </p>
                </div>
              </a>

              <a
                href="mailto:cmcoachandmotivation@protonmail.com"
                data-testid="contact-email"
                className="group flex items-start gap-4 sm:gap-5 py-6 border-t border-cm-line"
              >
                <span className="mt-1 w-10 h-10 rounded-full bg-cm-cream flex items-center justify-center text-cm-rose group-hover:bg-cm-rose group-hover:text-cm-cream transition-colors duration-500 flex-shrink-0">
                  <Mail size={16} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[0.7rem] tracking-[0.3em] uppercase text-cm-ink-soft">
                    Email
                  </p>
                  <p className="font-serif text-cm-ink text-base sm:text-xl mt-2 break-all sm:break-words group-hover:text-cm-rose transition-colors duration-500">
                    cmcoachandmotivation@protonmail.com
                  </p>
                </div>
              </a>

              <a
                href={FB_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-facebook"
                className="group flex items-start gap-4 sm:gap-5 py-6 border-t border-cm-line"
              >
                <span className="mt-1 w-10 h-10 rounded-full bg-cm-cream flex items-center justify-center text-cm-rose group-hover:bg-cm-rose group-hover:text-cm-cream transition-colors duration-500 flex-shrink-0">
                  <Facebook size={16} strokeWidth={1.5} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[0.7rem] tracking-[0.3em] uppercase text-cm-ink-soft">
                    Facebook
                  </p>
                  <p className="font-serif text-cm-ink text-lg sm:text-xl mt-2 group-hover:text-cm-rose transition-colors duration-500">
                    Følg CM — Coach &amp; Motivation
                  </p>
                </div>
              </a>

              <a
                href={IG_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram"
                className="group flex items-start gap-4 sm:gap-5 py-6 border-t border-cm-line"
              >
                <span className="mt-1 w-10 h-10 rounded-full bg-cm-cream flex items-center justify-center text-cm-rose group-hover:bg-cm-rose group-hover:text-cm-cream transition-colors duration-500 flex-shrink-0">
                  <Instagram size={16} strokeWidth={1.5} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[0.7rem] tracking-[0.3em] uppercase text-cm-ink-soft">
                    Instagram
                  </p>
                  <p className="font-serif text-cm-ink text-lg sm:text-xl mt-2 group-hover:text-cm-rose transition-colors duration-500">
                    @cm_coachandmotivation
                  </p>
                </div>
              </a>

              <div className="pt-6 border-t border-cm-line">
                <a
                  href="mailto:cmcoachandmotivation@protonmail.com?subject=Introsamtale"
                  data-testid="contact-cta-book"
                  className="cm-btn"
                >
                  Book en introsamtale
                  <span aria-hidden>→</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 sm:mt-24 pt-8 sm:pt-10 border-t border-cm-line flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs tracking-[0.22em] uppercase text-cm-ink-soft">
          <p data-testid="footer-brand" className="flex items-center gap-3">
            <img
              src="/brand/cm-logo.jpg"
              alt="CM"
              className="h-8 w-8 rounded-full object-cover ring-1 ring-cm-line"
            />
            <span className="w-px h-3 bg-cm-line" />
            <span>Coach &amp; Motivation</span>
          </p>
          <p data-testid="footer-tagline" className="font-serif italic normal-case tracking-normal text-cm-ink-soft">
            Fra reaktion til intention.
          </p>
          <p data-testid="footer-copy">© {year} Cristina Mona</p>
        </div>
      </div>
    </footer>
  );
}
